import { faArrowDownLong } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { isLotUnmanifested } from '@b-stock/fe-core/bstock-next/helpers'
import { type OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, Button } from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'

import {
  ScrollIntoViewId,
  useScrollIntoView,
} from '@/buyer-portal/components/features/listing/ScrollIntoView/ScrollIntoViewContext'
import { useDownloadManifest } from '@/buyer-portal/helpers/useDownloadManifest'
import { shouldShowManifestSection } from '@/helpers/listing/shouldShowManifestSection'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

const StyledButton = styled(Button)`
  ${Typography.Subtitle3}
`

export const Container = styled.div`
  display: flex;
  button {
    margin-right: 0.5rem;
  }
  justify-content: flex-end;
  white-space: nowrap;
`

const ManifestButtons = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const isLoggedIn = !!useDecodedAccessToken()?.personId
  const { formatMessage } = useIntl()
  const downloadManifest = useDownloadManifest()
  const { scrollIntoView } = useScrollIntoView()
  const { trackButtonClicked } = useAnalytics()

  if (
    !listing.lot._id ||
    !shouldShowManifestSection(listing) ||
    isLotUnmanifested(listing.lot)
  ) {
    return null
  }

  return (
    <Container>
      <StyledButton
        appearance="secondary"
        size="small"
        type="button"
        onClick={() => {
          trackButtonClicked({
            buttonName: 'view_manifest',
            seller_account_id: listing.accountId,
            storefront_name: listing.seller.storefront?.name,
            seller_display_name: listing.seller.account.displayName,
          })
          scrollIntoView(ScrollIntoViewId.MANIFEST)
        }}
      >
        <FormattedMessage id="Auction.details.manifest.actions.viewManifest" />
        <FontAwesomeIcon icon={faArrowDownLong} />
      </StyledButton>
      {isLoggedIn && (
        <StyledButton
          appearance="secondary"
          size="small"
          type="button"
          onClick={() => {
            trackButtonClicked({
              buttonName: 'download_manifest',
              seller_account_id: listing.accountId,
              storefront_name: listing.seller.storefront?.name,
              seller_display_name: listing.seller.account.displayName,
            })
            void downloadManifest(
              listing.lot._id,
              listing.lot.title || formatMessage({ id: 'Common.unknown' })
            )
          }}
        >
          <FormattedMessage id="Auction.details.manifest.actions.downloadManifest" />
        </StyledButton>
      )}
    </Container>
  )
}

export default ManifestButtons
